import * as Yup from "yup"
import { useTracker } from "@modules/analytics"
import { ui as uiText } from "@modules/locales/default.js"
import { Tag } from "@modules/graphql/types"
import { EnvVarsUiSource } from "../types"

export type EnvVarFieldValue = { key: string; value: string }

export type EnvVarsFieldName = `buildVariables` | `previewVariables`

export type FormValues = Record<EnvVarsFieldName, EnvVarFieldValue[]>

export type FormErrors = {
  [k: string]: string | undefined
}

export type EnvVarMap = { [key: string]: string }

export const baseFieldSchema = Yup.string().required(
  uiText.messages.validationCantBeEmpty
)

const envVarSchema = Yup.object<EnvVarFieldValue>().shape({
  key: baseFieldSchema,
  value: baseFieldSchema,
})

export const validationSchema = Yup.object<FormValues>().shape({
  buildVariables: Yup.array().of(envVarSchema),
  previewVariables: Yup.array().of(envVarSchema),
})

export function getInitialVariablesValue(envVars: Tag[]): EnvVarFieldValue[] {
  if (!envVars || envVars.length === 0) {
    return [{ key: ``, value: `` }]
  }
  return envVars.map(mapTagToFieldValue)
}

export function stripEmptyState(varsArray: EnvVarFieldValue[]) {
  if (varsArray.length === 1 && isEmptyFieldValue(varsArray[0])) {
    return []
  }
  return varsArray
}

function isEmptyFieldValue(envVar: EnvVarFieldValue) {
  return !envVar.key && !envVar.value
}

function mapTagToFieldValue(tag: Tag): EnvVarFieldValue {
  return {
    key: tag.key,
    value: tag.value,
  }
}

export function collectEnvVarsErrors(validationError: Yup.ValidationError) {
  const errors: { [k: string]: string | undefined } = {}
  let hasBuildVarsError = false
  let hasPreviewVarsError = false

  for (const fieldError of validationError.inner) {
    errors[fieldError.path] = fieldError.message
    if (!hasBuildVarsError && fieldError.path.startsWith("buildVariables")) {
      hasBuildVarsError = true
    }
    if (
      !hasPreviewVarsError &&
      fieldError.path.startsWith("previewVariables")
    ) {
      hasPreviewVarsError = true
    }
  }

  return {
    errors,
    hasBuildVarsError,
    hasPreviewVarsError,
  }
}

export function useEnvVarsFormAnalytics(
  siteId: string,
  uiSource: EnvVarsUiSource
) {
  const { trackButtonClicked } = useTracker()

  return {
    trackAddButtonClick: () => {
      trackButtonClicked(`Add environment variable`, {
        siteId,
        uiSource: uiSource,
      })
    },
    trackRemoveButtonClick: () => {
      trackButtonClicked(`Remove environment variable`, {
        siteId,
        uiSource: uiSource,
      })
    },
    trackBulkAddButtonClick: () => {
      trackButtonClicked(`Bulk add environment variables`, {
        siteId,
        uiSource: uiSource,
      })
    },
  }
}

/**
 * Converts an EnvVarFieldValue[] to a EnvVarMap with the EnvVarFieldValue's "key" and "value" properties
 */
export function envVarFieldsToMap(envVars: EnvVarFieldValue[]) {
  return envVars.reduce((map, { key, value }) => {
    map[key] = value
    return map
  }, {} as EnvVarMap)
}
